import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accrualHistoryService, AccrualHistoryFilters, CreateAccrualHistoryInput } from '@/services/leave/accrualHistoryService';
import { useToast } from '@/hooks/use-toast';

const ACCRUAL_HISTORY_QUERY_KEY = 'accrual-history';

export const useAccrualHistory = (filters?: AccrualHistoryFilters) => {
  return useQuery({
    queryKey: [ACCRUAL_HISTORY_QUERY_KEY, filters],
    queryFn: () => accrualHistoryService.getHistory(filters || {}),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

export const useAccrualHistorySummary = (filters?: AccrualHistoryFilters) => {
  return useQuery({
    queryKey: [ACCRUAL_HISTORY_QUERY_KEY, 'summary', filters],
    queryFn: () => accrualHistoryService.getHistorySummary(filters || {}),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCreateAccrualHistoryEntry = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (input: CreateAccrualHistoryInput) => 
      accrualHistoryService.recordEntry(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCRUAL_HISTORY_QUERY_KEY] });
      toast({
        title: 'History recorded',
        description: 'Accrual history entry has been recorded successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error recording history',
        description: error.message || 'Failed to record accrual history entry.',
        variant: 'destructive',
      });
    },
  });
};

export const accrualHistoryQueryKeys = {
  all: [ACCRUAL_HISTORY_QUERY_KEY] as const,
  lists: () => [...accrualHistoryQueryKeys.all, 'list'] as const,
  list: (filters?: AccrualHistoryFilters) => [...accrualHistoryQueryKeys.lists(), filters] as const,
  summaries: () => [...accrualHistoryQueryKeys.all, 'summary'] as const,
  summary: (filters?: AccrualHistoryFilters) => [...accrualHistoryQueryKeys.summaries(), filters] as const,
};
